import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { Grid, Button, CircularProgress } from '@material-ui/core';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import moment from 'moment';

import { PaginatedResponse } from '../../../shared/models';
import DateShortcutDropdown, { DateShortcut } from '../../components/DateShortcutDropdown';
import { getSalesByDateCommodityGroupReport, getLeafCommodityGroups } from '../../api'; // Updated API call and new import
import { checkStatus, parseJSON, handleError } from '../../../shared/api/core/common';
import { SVG_ICONS } from '../../../shared/icons/SvgIcons';
import { ErrorStatusBar, CustomTableTitle, ExportMenu, ExportStatusBar, } from '../../../shared/components';
import { EMPTY_ROW_MSG, ERROR_500, ERROR_MSG } from '../../../shared/lib/Localization';
import { initQueryParams, replaceHistory, tableOptions, useQuery } from '../../../shared/lib/TableQueryParams';
import download from 'downloadjs';
import GlobalStyles from '../../../../styles/GlobalStyles.web';
import MaterialTable, { MTableToolbar } from 'material-table';
import { FormControl, InputLabel, Select, MenuItem, TextField, Checkbox, FormControlLabel } from '@material-ui/core'; // New imports for dropdown, TextField, Checkbox, FormControlLabel
import Autocomplete from '@material-ui/lab/Autocomplete'; // New import for Autocomplete
import { enqueueSnackbar, Toast, ToastError } from '../../../shared/components/Notifier';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paper: {
      padding: theme.spacing(3),
      marginBottom: theme.spacing(2),
    },
    formControl: {
      minWidth: 120,
      width: '100%',
    },
    progress: {
      margin: theme.spacing(2),
    },
    select: {
      color: 'black',
      marginRight: 4,
      '&:before': {
        borderColor: GlobalStyles.primaryColor,
      },
      '&:after': {
        borderColor: GlobalStyles.primaryColor,
      },
      maxWidth: '15rem',
      minWidth: '12rem',
    },
    divider: {
      margin: theme.spacing(0.5, 0),
    },
    filterButtonContainer: {
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(1),
        marginBottom: theme.spacing(2),
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'relative', // Add relative positioning
        zIndex: 'auto', // Ensure z-index doesn't interfere
    },
  }),
);


const SalesByDateCommodityGroupReport = (props: any) => {
  const classes = useStyles({});
  let tableRef = useRef<{ onQueryChange: any; onChangePage: any; }>();
  const [emptyDataSourceMessage, setEmptyDataSourceMessage] = useState(EMPTY_ROW_MSG);
  let locationQuery = useQuery(useLocation);
  const [tableQuery, setTableQuery] = useState({ ...initQueryParams(locationQuery), data: [] as any[] });
  const [isLoading, setLoading] = useState(false);
  const [isExporting, setExporting] = useState(false);
  const [exportBtnAnchorEl, setExportBtnAnchorEl] = React.useState<null | HTMLElement>(null);
  const [shouldFetchReport, setShouldFetchReport] = useState(false); // New state to control report fetching
  const [showOnlyRowsWithSales, setShowOnlyRowsWithSales] = useState(true); // New state for the checkbox
  
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [currentShortcut, setCurrentShortcut] = useState<string>('today');
  const [tableColumns, setTableColumns] = useState<any[]>([]);
  const [commodityGroups, setCommodityGroups] = useState<any[]>([]); // Initialize as empty array
  const [selectedCommodityGroup, setSelectedCommodityGroup] = useState<any | null>(null); // New state for selected commodity group, now stores object


  const formatDateForAPI = (date: Date | null): string => {
    if (!date) return '';
    return moment(date).format('YYYY-MM-DDTHH:mm:ss'); // Changed format for backend
  };

  // Helper functions for date calculations
  const getStartOfDay = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
  const getEndOfDay = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);

  // All shortcut options
  const allShortcutOptions = [
    { value: DateShortcut.Custom, label: 'Custom Range' },
    { value: DateShortcut.Today, label: 'Today', getRange: () => ({ startDate: getStartOfDay(new Date()), endDate: getEndOfDay(new Date()) }) },
    { value: DateShortcut.Yesterday, label: 'Yesterday', getRange: () => {
        const yesterdayDt = new Date();
        yesterdayDt.setDate(yesterdayDt.getDate() - 1);
        return { startDate: getStartOfDay(yesterdayDt), endDate: getEndOfDay(yesterdayDt) };
    }},
    { value: 'last_7_days', label: 'Last 7 Days', getRange: () => {
        const last7DaysDt = new Date();
        last7DaysDt.setDate(last7DaysDt.getDate() - 6); // Last 7 days includes today
        return { startDate: getStartOfDay(last7DaysDt), endDate: getEndOfDay(new Date()) };
    }},
    { value: 'last_14_days', label: 'Last 14 Days', getRange: () => {
        const last14DaysDt = new Date();
        last14DaysDt.setDate(last14DaysDt.getDate() - 13); // Last 14 days includes today
        return { startDate: getStartOfDay(last14DaysDt), endDate: getEndOfDay(new Date()) };
    }},
    { value: 'last_30_days', label: 'Last 30 Days', getRange: () => {
        const last30DaysDt = new Date();
        last30DaysDt.setDate(last30DaysDt.getDate() - 29); // Last 30 days includes today
        return { startDate: getStartOfDay(last30DaysDt), endDate: getEndOfDay(new Date()) };
    }},
  ];

  const exportCSV = () => {
    if (!selectedCommodityGroup) {
      props.showToast({ message: 'Please select a Commodity Group to export.', options: ToastError });
      return;
    }
    if (props.userData && props.userData.selectedBusiness) {
      setExporting(true);
      const sb = props.userData.selectedBusiness;
      const location_ids = props.userData && props.userData.selectedLocations ? props.userData.selectedLocations.map((l: any) => l.id) : [];
      const location_ids_str = location_ids.join(','); // Comma separated location ids
      const shouldExport = true;
      getSalesByDateCommodityGroupReport( // Updated API call
        sb.business_id,
        location_ids_str,
        formatDateForAPI(startDate),
        formatDateForAPI(endDate),
        selectedCommodityGroup.number, // New parameter
        null,
        null,
        tableQuery.search,
        tableQuery.orderBy,
        shouldExport,
        showOnlyRowsWithSales // New parameter
      )
        .catch(handleError)
        .then(checkStatus)
        .then((res) => {
          let filename = 'sales_by_date_commodity_group_report.csv';
          const contentDisposition = res.headers.get('Content-Disposition');
          if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename\*?=['"]?(?:UTF-\d['"]*)?([^;\n]*?)['"]?$/i);
            if (filenameMatch && filenameMatch[1]) {
              filename = decodeURIComponent(filenameMatch[1].replace(/^['"]|['"]$/g, ''));
              // Further clean up by removing leading/trailing underscores if present
              filename = filename.replace(/^_|_$/g, '');
            }
          }
          return Promise.all([res.blob(), filename]);
        })
        .then(([data, filename]) => {
          download(data, filename, 'text/csv');
          setExporting(false);
        })
        .catch((error: any) => {
          setExporting(false);
          if (error) {
            if (error.status && error.status === 500) {
              props.showToast({ message: ERROR_MSG, options: ToastError });
            } else if (error.status === 403 || error.status === 401) {
              props.showToast({ message: ERROR_MSG, options: ToastError });
            } else {
              props.showToast({ message: ERROR_MSG, options: ToastError });
            }
          }
        });
    }
  };

  const handleExportClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    console.log('Export button clicked, event.currentTarget:', event.currentTarget);
    console.log('Event target element:', event.currentTarget.tagName, event.currentTarget.className);
    console.log('Element position:', event.currentTarget.getBoundingClientRect());
    setExportBtnAnchorEl(event.currentTarget);
    console.log('Anchor element set to:', event.currentTarget);
  };

  const handleExportMenuClose = (shouldExport: boolean) => {
    if (shouldExport) {
      exportCSV();
    }
    setExportBtnAnchorEl(null);
  };

  const handleGenerateReport = () => {
      if (!selectedCommodityGroup) {
          props.showToast({ message: 'Please select a Commodity Group.', options: ToastError });
          return;
      }
      setShouldFetchReport(true); // Trigger report fetch
      if (tableRef.current) {
          tableRef.current.onQueryChange();
      }
  }

  useEffect(() => {
    const sb = props.userData.selectedBusiness;
    // Fetch commodity groups
    getLeafCommodityGroups(sb.business_id)
      .then(checkStatus)
      .then(parseJSON)
      .then((data: any) => {
        setCommodityGroups(data || []); // Ensure it's always an array
      })
      .catch((error) => {
        handleError(error);
        setCommodityGroups([]); // Set to empty array on error
      });
  }, [props?.userData?.selectedBusiness])
  
  useEffect(() => {
    // Dynamically generate columns based on the data structure
    // This effect will run when data is fetched and available
    if (shouldFetchReport && tableQuery.data && tableQuery.data.length > 0) {
      const firstRow = tableQuery.data[0];
      const dynamicColumns = Object.keys(firstRow)
        .filter(key => key !== 'number' && key !== 'product')
        .map(key => ({
          title: key, // Date as title
          field: key, // Date as field
          type: 'numeric',
          width: 50,
          render: (rowData: any) => rowData[key] || 0, // Display 0 if no sales
        }));

      setTableColumns([
        { title: 'Number', field: 'number', minWidth: 100, fixed: 'left' },
        { title: 'Product', field: 'product', minWidth: 400, fixed: 'left' },
        ...dynamicColumns,
      ]);
    } else if (shouldFetchReport) { // Only reset columns if a fetch was attempted
      setTableColumns([
        { title: 'Number', field: 'number', minWidth: 100, fixed: 'left' },
        { title: 'Product', field: 'product', minWidth: 400, fixed: 'left' },
      ]);
    }
  }, [tableQuery.data, shouldFetchReport]);


  return (
    <div>
      <ExportStatusBar isVisible={isExporting} />

      <div style={{ maxWidth: '100%' }}>
        
        
        <div className="table-wrapper">
          <MaterialTable
            options={{
              ...tableOptions(tableQuery),
              thirdSortClick: false,
              draggable: false,
              sorting: true
            }}
            tableRef={tableRef}
            localization={{
              body: {
                emptyDataSourceMessage: emptyDataSourceMessage
              }
            }}
            columns={tableColumns}
            data={query =>
              new Promise((resolve, reject) => {
                if (!shouldFetchReport || !startDate || !endDate || !selectedCommodityGroup) {
                    setLoading(false);
                    resolve({
                        data: [],
                        page: 0,
                        totalCount: 0,
                    });
                    // Do not show error on initial load if no commodity group is selected
                    if (shouldFetchReport && !selectedCommodityGroup) {
                        props.showToast({ message: 'Please select a Commodity Group.', options: ToastError });
                    } else {
                        // No need to set emptyDataSourceMessage or setErrorStatusVisible here
                    }
                    return;
                }
                replaceHistory(query, props.history);
                let pageData = {
                  data: [] as any[],
                  totalCount: 0,
                  page: query.page
                };
                let orderBy = '';
                if (query.orderBy) {
                    let field = query.orderBy.field;
                    if (field === 'number') {
                        orderBy = 'number';
                    } else if (field === 'product') {
                        orderBy = 'product';
                    }
                    // No specific sorting for dynamic date columns from backend
                    if (orderBy) {
                        orderBy = `${query.orderDirection && query.orderDirection === 'desc' ? '-' : ''}${orderBy}`;
                    }
                }
                setLoading(true);
                const sb = props.userData.selectedBusiness;
                const location_ids = props.userData && props.userData.selectedLocations ? props.userData.selectedLocations.map((l: any) => l.id) : [];
                const location_ids_str = location_ids.join(','); // Comma separated location ids

                getSalesByDateCommodityGroupReport( // Updated API call
                  sb.business_id,
                  location_ids_str,
                  formatDateForAPI(startDate),
                  formatDateForAPI(endDate),
                  selectedCommodityGroup.number, // Use selectedCommodityGroup.number
                  query.page + 1,
                  query.pageSize,
                  query.search,
                  orderBy,
                  false, // shouldExport  
                  showOnlyRowsWithSales // New parameter
                )
                  .catch(handleError)
                  .then(checkStatus)
                  .then(parseJSON)
                  .then((data: PaginatedResponse<any>) => {
                    setLoading(false);
                    // No need to set emptyDataSourceMessage or setErrorStatusVisible here
                    pageData = {
                      data: data.results,
                      totalCount: data.count,
                      page: query.page
                    };
                    setTableQuery({
                      ...tableQuery,
                      orderBy,
                      totalCount: data.count,
                      page: query.page,
                      pageSize: query.pageSize,
                      data: data.results, // Store data to dynamically generate columns
                    });
                    resolve(pageData);
                  })
                  .catch((error: any) => {
                    if (error) {
                      if (error.status && error.status === 500) {
                        props.showToast({ message: ERROR_500, options: ToastError });
                      } else if ((error.status === 403 || error.status === 401)
                      ) {
                        props.showToast({ message: ERROR_MSG, options: ToastError });
                      } else {
                        props.showToast({ message: ERROR_MSG, options: ToastError });
                      }
                    }
                    setTableQuery({
                      ...tableQuery,
                      totalCount: 0,
                      page: query.page,
                      pageSize: query.pageSize,
                      data: [],
                    });
                    setLoading(false);

                    resolve(pageData);
                  });
              })
            }
            title={
              <CustomTableTitle
                history={props.history}
                title={'Sales by Date for Commodity Group'}
                icon={SVG_ICONS.reports} />
            }
            components={{
                Toolbar: (toolbarProps) => (
                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <>
                            <MTableToolbar {...toolbarProps} />
                            <Grid container spacing={2} alignItems="center" className={classes.filterButtonContainer}>
                                <Grid item>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item>
                                            <DateShortcutDropdown
                                                key={`current-${currentShortcut}`}
                                                label="Date Shortcuts"
                                                setStartDate={(newDate) => setStartDate(currentDate => {
                                                    if (newDate === null && currentDate === null) return currentDate;
                                                    if (newDate === null || currentDate === null) return newDate;
                                                    if (newDate.getTime() !== currentDate.getTime()) return newDate;
                                                    return currentDate;
                                                })}
                                                setEndDate={(newDate) => setEndDate(currentDate => {
                                                    if (newDate === null && currentDate === null) return currentDate;
                                                    if (newDate === null || currentDate === null) return newDate;
                                                    if (newDate.getTime() !== currentDate.getTime()) return newDate;
                                                    return currentDate;
                                                })}
                                                value={currentShortcut}
                                                onChange={(newShortcut) => {
                                                    if (newShortcut !== currentShortcut) {
                                                        setCurrentShortcut(newShortcut);
                                                    }
                                                }}
                                                className={classes.formControl}
                                                shortcutOptions={allShortcutOptions}
                                            />
                                        </Grid>
                                        {currentShortcut === DateShortcut.Custom && (
                                            <Grid item>
                                                <Grid container spacing={1}>
                                                    <Grid item>
                                                        <KeyboardDatePicker
                                                            fullWidth
                                                            label="Start Date"
                                                            value={startDate}
                                                            onChange={(date) => setStartDate(date ? new Date(date as any) : null)}
                                                            format="yyyy-MM-dd"
                                                            className={classes.formControl}
                                                            autoOk
                                                            openTo="month"
                                                            views={['year', 'month', 'date']}
                                                            maxDate={new Date()}
                                                            maxDateMessage="Date cannot be in the future"
                                                        />
                                                    </Grid>
                                                    <Grid item>
                                                        <KeyboardDatePicker
                                                            fullWidth
                                                            label="End Date"
                                                            value={endDate}
                                                            onChange={(date) => setEndDate(date ? new Date(date as any) : null)}
                                                            format="yyyy-MM-dd"
                                                            className={classes.formControl}
                                                            autoOk
                                                            openTo="month"
                                                            views={['year', 'month', 'date']}
                                                            maxDate={new Date()}
                                                            maxDateMessage="Date cannot be in the future"
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        )}
                                        <Grid item>
                                            {commodityGroups && commodityGroups.length > 0 && (
                                                <Autocomplete
                                                    options={commodityGroups}
                                                    getOptionLabel={(option: any) => option.name || ''}
                                                    value={selectedCommodityGroup}
                                                    onChange={(event, newValue) => {
                                                        setSelectedCommodityGroup(newValue);
                                                    }}
                                                    style={{ width: 400 }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Commodity Group"
                                                            variant="standard"
                                                            className={classes.formControl}
                                                        />
                                                    )}
                                                />
                                            )}
                                        </Grid>
                                        <Grid item>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={showOnlyRowsWithSales}
                                                        onChange={(event) => setShowOnlyRowsWithSales(event.target.checked)}
                                                        name="showOnlyRowsWithSales"
                                                        color="primary"
                                                    />
                                                }
                                                label="Show only rows with sales"
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleGenerateReport}
                                        disabled={isLoading}
                                        style={{ marginRight: '10px' }}
                                    >
                                        {isLoading ? <CircularProgress size={24} /> : 'Generate Report'}
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        onClick={exportCSV}
                                        disabled={isExporting}
                                    >
                                        {isExporting ? <CircularProgress size={24} /> : 'Export CSV'}
                                    </Button>
                                </Grid>
                            </Grid>
                        </>
                    </MuiPickersUtilsProvider>
                ),
            }}
          />
        </div>
      </div>
      <ExportMenu anchorEl={exportBtnAnchorEl} handleClose={handleExportMenuClose} />
    </div>
  );
};

const mapStateToProps = (state: any) => {
  return {
    userData: state.userLocalData,
  };
};

const mapDispatchToProps = (dispatch: any) => {
  return {
    showToast: (toast: Toast) => dispatch(enqueueSnackbar(toast)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(SalesByDateCommodityGroupReport);
